// Chunked video upload for Pano slots: any length, resumable, with a still frame for thumbs/PDF.
import { api } from "@/lib/api";
import { enqueue } from "@/lib/offlineDb";

const CHUNK = 4 * 1024 * 1024;
const VIDEO_EXT = new Set(["mp4", "mov", "m4v", "webm", "3gp", "avi", "mkv"]);

export function isVideoFile(file) {
  const type = (file?.type || "").toLowerCase();
  if (type.startsWith("video/")) return true;
  if (type) return false;
  const ext = (file?.name || "").split(".").pop().toLowerCase();
  return VIDEO_EXT.has(ext);
}

// Grab a frame ~1s in (or the first frame for short clips). Resolves null if the browser can't decode it.
export function makePoster(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    let settled = false;
    const done = (blob) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(blob);
    };
    const timer = setTimeout(() => done(null), 10000);
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.onloadeddata = () => {
      v.currentTime = Math.min(1, (v.duration || 0) / 2);
    };
    v.onseeked = () => {
      try {
        const w = v.videoWidth || 1280;
        const h = v.videoHeight || 720;
        const scale = Math.min(1, 1920 / Math.max(w, h));
        const c = document.createElement("canvas");
        c.width = Math.round(w * scale);
        c.height = Math.round(h * scale);
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        c.toBlob((b) => { clearTimeout(timer); done(b); }, "image/jpeg", 0.85);
      } catch {
        clearTimeout(timer);
        done(null);
      }
    };
    v.onerror = () => { clearTimeout(timer); done(null); };
    v.src = url;
  });
}

const isTransient = (err) => {
  const s = err?.response?.status;
  return !err?.response || [429, 502, 503, 504].includes(s);
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function sendChunk(projectId, uploadId, index, blob, category) {
  for (let attempt = 0; ; attempt += 1) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(index));
    fd.append("category", category);
    fd.append("chunk", blob, `part${index}`);
    try {
      return await api.post(`/projects/${projectId}/video/chunk`, fd, { timeout: 180_000 });
    } catch (err) {
      if (!isTransient(err) || attempt >= 3) throw err;
      await sleep(1500 * 2 ** attempt);
    }
  }
}

// Resumes from the pieces the server already has. `onProgress(0..100)`.
export async function uploadVideoChunked({ projectId, file, fields, onProgress }) {
  const uploadId = fields.client_upload_id;
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  let have = new Set();
  try {
    const r = await api.get(`/projects/${projectId}/video/${uploadId}/received`);
    have = new Set(r.data?.received || []);
  } catch (err) {
    if (!err?.response) throw err;
  }
  let sent = have.size;
  onProgress?.(Math.round((sent / total) * 95));
  for (let i = 0; i < total; i += 1) {
    if (have.has(i)) continue;
    // eslint-disable-next-line no-await-in-loop
    await sendChunk(projectId, uploadId, i, file.slice(i * CHUNK, (i + 1) * CHUNK), fields.category);
    sent += 1;
    onProgress?.(Math.round((sent / total) * 95));
  }
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && k !== "client_upload_id" && k !== "kind") fd.append(k, v);
  });
  fd.append("upload_id", uploadId);
  fd.append("total", String(total));
  fd.append("filename", file.name || "video.mp4");
  fd.append("content_type", file.type || "video/mp4");
  const poster = await makePoster(file);
  if (poster) fd.append("poster", poster, "poster.jpg");
  const r = await api.post(`/projects/${projectId}/video/complete`, fd, { timeout: 900_000 });
  onProgress?.(100);
  return r.data;
}

// Live attempt first; on a connection/storage hiccup, save the video on the device for the sync worker.
export async function uploadVideoOffline({ projectId, file, fields, label, onProgress }) {
  const uploadId = fields.client_upload_id ||
    (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  fields = { ...fields, client_upload_id: uploadId };
  try {
    const record = await uploadVideoChunked({ projectId, file, fields, onProgress });
    return { ok: true, record };
  } catch (err) {
    if (!isTransient(err)) throw err;
    const queueId = await enqueue({
      kind: "video",
      projectId,
      formFields: fields,
      file,
      filename: file.name || "video.mp4",
      label,
      contentType: file.type || "video/mp4",
    });
    window.dispatchEvent(new CustomEvent("f2:queue-changed"));
    return { ok: true, queued: true, queueId };
  }
}
